import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import API from '../api';
import ProductCard from '../components/ProductCard';
import ProductGrid from '../components/ProductGrid';
import FilterSidebar from '../components/FilterSidebar';
import { MdViewModule, MdViewList } from 'react-icons/md';
import { SellerProfileSkeleton } from '../components/skeleton';
import ReCAPTCHA from 'react-google-recaptcha';
import './SellerProfilePage.css';

const SellerProfilePage = () => {
  const { id, storeHandle } = useParams();
  const [seller, setSeller] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    sort: 'relevance', // Ordinamento di default: rilevanza
    price: [0, 0],
    brands: [],
  });
  const [filterData, setFilterData] = useState({
    min_price: 0,
    max_price: 0,
    brands: [],
  });
  const [view, setView] = useState('grid');
  const [activeTab, setActiveTab] = useState('products');
  const [loadingSeller, setLoadingSeller] = useState(true);
  const [filtersReady, setFiltersReady] = useState(false);
  const [showAllBrands, setShowAllBrands] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [pendingFilters, setPendingFilters] = useState(null);
  const [sellerNotFound, setSellerNotFound] = useState(false);

  // Stati per il form di contatto
  const [contactFormData, setContactFormData] = useState({
    nome: '',
    email: '',
    oggetto: '',
    messaggio: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [captchaToken, setCaptchaToken] = useState('');
  const [contactMessage, setContactMessage] = useState('');

  useEffect(() => {
    const fetchSeller = async () => {
      try {
        const { data } = await API.get(`/seller/${storeHandle}`);
        if (data.success) {
          setSeller(data.seller);
        }
      } catch (error) {
        console.error('Errore durante il recupero del profilo venditore:', error);
        // Se l'errore è 404 (venditore non trovato), imposta il flag
        if (error.response && error.response.status === 404) {
          setSellerNotFound(true);
        }
      } finally {
        setLoadingSeller(false);
      }
    };
    fetchSeller();
  }, [storeHandle]);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const res = await API.get(`/seller/${storeHandle}/filters`);
        if (res.data.success) {
          setFilterData({
            min_price: res.data.min_price || 0,
            max_price: res.data.max_price || 0,
            brands: res.data.brands || [],
          });
          setFilters(f => ({
            ...f,
            price: [res.data.min_price || 0, res.data.max_price || 0],
          }));
          setFiltersReady(true);
        }
      } catch (e) {
        // fallback
        setFiltersReady(true); // fallback: evita blocco
      }
    };
    fetchFilters();
  }, [storeHandle]);

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
  };

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFilters((prev) => ({
        ...prev,
        [name]: checked
          ? [...prev[name], value]
          : prev[name].filter((v) => v !== value),
      }));
    } else {
      setFilters((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handlePriceChange = (e, idx) => {
    const val = Number(e.target.value);
    setFilters((prev) => {
      const newPrice = [...prev.price];
      newPrice[idx] = val;
      return { ...prev, price: newPrice };
    });
  };

  const handleSortChange = (e) => {
    setFilters((prev) => ({ ...prev, sort: e.target.value }));
  };

  // Applica i filtri dal modal mobile
  const handleApplyMobileFilters = () => {
    if (pendingFilters) setFilters(pendingFilters);
    setShowMobileFilters(false);
  };

  // Gestione form di contatto
  const handleContactInputChange = (e) => {
    const { name, value } = e.target;
    setContactFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!captchaToken) {
      setSubmitStatus('recaptcha_error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await API.post(`/contacts/seller/${storeHandle}`, {
        ...contactFormData,
        captchaToken
      });
      
      if (response.data.success) {
        setSubmitStatus('success');
        setContactFormData({ nome: '', email: '', oggetto: '', messaggio: '' });
        setCaptchaToken('');
        setContactMessage('');
      }
    } catch (error) {
      console.error('Errore nell\'invio del form di contatto:', error);
      setContactMessage(error.response ? error.response.data.message : 'Errore di rete');
      if (error.response && error.response.data.message === 'reCAPTCHA non valido') {
        setSubmitStatus('recaptcha_error');
        setCaptchaToken('');
      } else {
        setSubmitStatus('error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingSeller) {
    return <SellerProfileSkeleton />;
  }

  // Se il venditore non è stato trovato, reindirizza alla pagina 404
  if (sellerNotFound) {
    return <Navigate to="/404" replace />;
  }

  if (!seller) {
    return <Navigate to="/404" replace />;
  }

  return (
    <div className="main-content-container seller-profile-page">
      {/* Banner con logo e informazioni */}
      <div className="seller-profile-banner">
        {seller.store_banner && (
          <div
            className="seller-profile-banner-img desktop-only"
            style={{ backgroundImage: `url(${seller.store_banner})` }}
          ></div>
        )}
        <div className="seller-profile-banner-content">
          <img
            src={seller.store_logo_square}
            alt={seller.store_name}
            className="seller-profile-logo"
          />
          <div>
            <h2 className="seller-profile-title">{seller.store_name}</h2>
            <p className="seller-profile-description">{seller.store_description}</p>
            {seller.website && (
              <a
                href={`${seller.website}?utm_source=loverlist&utm_medium=profile&utm_campaign=visit_store`}
                className="seller-profile-visit-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                Visita il sito
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Menu di navigazione */}
      <div className="seller-profile-tabs">
        {[
          { id: 'products', label: 'Prodotti' },
          { id: 'details', label: 'Dettagli negozio' },
          { id: 'contact', label: 'Contatta' },
          { id: 'reviews', label: 'Recensioni' },
        ].map(tab => (
          <button
            key={tab.id}
            className={`seller-profile-tab-pill${activeTab === tab.id ? ' active' : ''}`}
            onClick={() => handleTabClick(tab.id)}
            type="button"
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="tab-content">
        {/* Tab Prodotti */}
        <div className={`tab-pane${activeTab === 'products' ? ' active' : ''}`} id="products">
          <div className="seller-profile-products-row">
            {/* Sidebar filtri (desktop) */}
            <div className="seller-profile-sidebar-desktop">
              <FilterSidebar
                filterData={filterData}
                filters={filters}
                onFilterChange={handleFilterChange}
                onPriceChange={handlePriceChange}
                showAllBrands={showAllBrands}
                setShowAllBrands={setShowAllBrands}
                showAllVendors={false}
                setShowAllVendors={() => {}}
              />
            </div>
            {/* Modal filtri mobile */}
            {showMobileFilters && (
              <div className="mobile-filters-modal">
                <div className="mobile-filters-overlay" onClick={() => setShowMobileFilters(false)} />
                <div className="mobile-filters-sheet">
                  <FilterSidebar
                    filterData={filterData}
                    filters={pendingFilters || filters}
                    onFilterChange={e => {
                      const { name, value, type, checked } = e.target;
                      setPendingFilters(prev => {
                        if (!prev) return prev;
                        if (type === 'checkbox') {
                          return {
                            ...prev,
                            [name]: checked
                              ? [...prev[name], value]
                              : prev[name].filter((v) => v !== value),
                          };
                        } else {
                          return { ...prev, [name]: value };
                        }
                      });
                    }}
                    onPriceChange={(e, idx) => {
                      const val = Number(e.target.value);
                      setPendingFilters(prev => {
                        if (!prev) return prev;
                        const newPrice = [...prev.price];
                        newPrice[idx] = val;
                        return { ...prev, price: newPrice };
                      });
                    }}
                    showAllBrands={showAllBrands}
                    setShowAllBrands={setShowAllBrands}
                    showAllVendors={false}
                    setShowAllVendors={() => {}}
                    isMobile={true}
                    onApply={handleApplyMobileFilters}
                    onClose={() => setShowMobileFilters(false)}
                  />
                </div>
              </div>
            )}
            {/* Griglia/lista prodotti */}
            <div className="seller-profile-products-main">
              {/* Toolbar mobile (max-width: 991px) */}
              <div className="products-toolbar toolbar-mobile-custom">
                <div className="toolbar-mobile-row1">
                  <button
                    className="filter-btn"
                    onClick={() => {
                      setPendingFilters({ ...filters });
                      setShowMobileFilters(true);
                    }}
                  >
                    <span className="filter-icon" /> Filtri
                  </button>
                  <input
                    type="text"
                    className="products-search-input"
                    placeholder="Cerca tra i prodotti del venditore..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="toolbar-mobile-row2">
                  <div className="sort-pill">
                    <select
                      className="sort-select"
                      name="sort"
                      value={filters.sort}
                      onChange={handleSortChange}
                    >
                      <option value="relevance">Rilevanza</option>
                      <option value="date_desc">Data: più recente</option>
                      <option value="date_asc">Data: meno recente</option>
                      <option value="price_asc">Prezzo: dal più basso</option>
                      <option value="price_desc">Prezzo: dal più alto</option>
                      <option value="title_asc">Titolo: A-Z</option>
                      <option value="title_desc">Titolo: Z-A</option>
                    </select>
                    <span className="sort-arrow">
                      <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M6 8l4 4 4-4" stroke="#c93249" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </span>
                  </div>
                  <div className="view-toggle-pill">
                    <div className="view-toggle-group">
                      <button
                        className={`view-toggle${view === 'grid' ? ' active' : ''}`}
                        onClick={() => setView('grid')}
                        type="button"
                        aria-label="Vista griglia"
                      >
                        <MdViewModule size={22} />
                      </button>
                      <button
                        className={`view-toggle${view === 'list' ? ' active' : ''}`}
                        onClick={() => setView('list')}
                        type="button"
                        aria-label="Vista lista"
                      >
                        <MdViewList size={22} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              {/* Toolbar desktop (min-width: 992px) */}
              <div className="products-toolbar toolbar-desktop-only">
                <input
                  type="text"
                  className="products-search-input"
                  placeholder="Cerca tra i prodotti del venditore..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <label className="sort-label">Ordina per</label>
                <div className="sort-pill">
                  <select
                    className="sort-select"
                    name="sort"
                    value={filters.sort}
                    onChange={handleSortChange}
                  >
                    <option value="relevance">Rilevanza</option>
                    <option value="date_desc">Data: più recente</option>
                    <option value="date_asc">Data: meno recente</option>
                    <option value="price_asc">Prezzo: dal più basso</option>
                    <option value="price_desc">Prezzo: dal più alto</option>
                    <option value="title_asc">Titolo: A-Z</option>
                    <option value="title_desc">Titolo: Z-A</option>
                  </select>
                  <span className="sort-arrow">
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M6 8l4 4 4-4" stroke="#c93249" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </span>
                </div>
                <div className="view-toggle-pill">
                  <div className="view-toggle-group">
                    <button
                      className={`view-toggle${view === 'grid' ? ' active' : ''}`}
                      onClick={() => setView('grid')}
                      type="button"
                      aria-label="Vista griglia"
                    >
                      <MdViewModule size={22} />
                    </button>
                    <button
                      className={`view-toggle${view === 'list' ? ' active' : ''}`}
                      onClick={() => setView('list')}
                      type="button"
                      aria-label="Vista lista"
                    >
                      <MdViewList size={22} />
                    </button>
                  </div>
                </div>
              </div>
              {filtersReady && (
                <ProductGrid
                  mode="seller"
                  sellerHandle={storeHandle}
                  filters={{ ...filters, search: searchTerm }}
                  view={view}
                />
              )}
            </div>
          </div>
        </div>

        {/* Tab Dettagli del negozio */}
        <div className={`tab-pane${activeTab === 'details' ? ' active' : ''}`} id="details">
          <h3>Dettagli del negozio</h3>
          <p>{seller.store_description}</p>
          <p><strong>Indirizzo:</strong> {seller.address}, {seller.city}, {seller.zip}, {seller.province}, {seller.country}</p>
          <p><strong>Sito Web:</strong> <a href={seller.website} target="_blank" rel="noopener noreferrer">{seller.website}</a></p>
          <p><strong>Partita IVA:</strong> {seller.vat_number}</p>
          <p><strong>Codice Univoco:</strong> {seller.univocal_code}</p>
          <p><strong>PEC:</strong> {seller.pec}</p>
        </div>

        {/* Tab Contatta il venditore */}
        <div className={`tab-pane${activeTab === 'contact' ? ' active' : ''}`} id="contact">
          <h3>Contatta il venditore</h3>
          <form className="seller-profile-contact-form" onSubmit={handleContactSubmit}>
            <div className="seller-profile-form-group">
              <label className="seller-profile-form-label">Nome completo *</label>
              <input 
                type="text" 
                className="seller-profile-form-input" 
                name="nome"
                value={contactFormData.nome}
                onChange={handleContactInputChange}
                required
              />
            </div>
            <div className="seller-profile-form-group">
              <label className="seller-profile-form-label">Email *</label>
              <input 
                type="email" 
                className="seller-profile-form-input" 
                name="email"
                value={contactFormData.email}
                onChange={handleContactInputChange}
                required
              />
            </div>
            <div className="seller-profile-form-group">
              <label className="seller-profile-form-label">Oggetto *</label>
              <select 
                className="seller-profile-form-input" 
                name="oggetto"
                value={contactFormData.oggetto}
                onChange={handleContactInputChange}
                required
              >
                <option value="">Seleziona un oggetto</option>
                <option value="informazioni-prodotto">Informazioni sul prodotto</option>
                <option value="disponibilita">Disponibilità</option>
                <option value="prezzo">Richiesta prezzo</option>
                <option value="spedizione">Informazioni spedizione</option>
                <option value="reso">Reso e garanzia</option>
                <option value="altro">Altro</option>
              </select>
            </div>
            <div className="seller-profile-form-group">
              <label className="seller-profile-form-label">Messaggio *</label>
              <textarea 
                className="seller-profile-form-textarea" 
                rows="5"
                name="messaggio"
                value={contactFormData.messaggio}
                onChange={handleContactInputChange}
                placeholder="Scrivi il tuo messaggio qui... (minimo 10 caratteri)"
                required
              ></textarea>
              <div className="form-character-count">
                {contactFormData.messaggio.length < 10 ? (
                  <span className="character-count-error">
                    Almeno {10 - contactFormData.messaggio.length} caratteri rimanenti
                  </span>
                ) : (
                  <span className="character-count-success">
                    {contactFormData.messaggio.length} caratteri
                  </span>
                )}
              </div>
            </div>
            <div className="seller-profile-form-group">
              <div className="seller-profile-captcha">
                <ReCAPTCHA
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  onChange={(token) => setCaptchaToken(token)}
                />
              </div>
            </div>
            <button 
              type="submit" 
              className="seller-profile-form-btn"
              disabled={isSubmitting || !captchaToken}
            >
              {isSubmitting ? 'Invio in corso...' : 'Invia messaggio'}
            </button>
          </form>

          {/* Message box */}
          {submitStatus === 'success' && (
            <div className="seller-profile-success-message">
              <p>Grazie per aver contattato il venditore! Ti risponderà il prima possibile.</p>
            </div>
          )}
          
          {submitStatus === 'error' && (
            <div className="seller-profile-error-message">
              <p>Errore di rete</p>
            </div>
          )}
          
          {submitStatus === 'recaptcha_error' && (
            <div className="seller-profile-error-message">
              <p>Completa il reCAPTCHA.</p>
            </div>
          )}

          {contactMessage && (
            <div className="seller-profile-error-message">
              <p>{contactMessage}</p>
            </div>
          )}
        </div>

        {/* Tab Recensioni */}
        <div className={`tab-pane${activeTab === 'reviews' ? ' active' : ''}`} id="reviews">
          <h3>Recensioni</h3>
          <p>Ancora nessuna recensione.</p>
        </div>
      </div>
    </div>
  );
};

export default SellerProfilePage;
